// 3-call chain Bearer auth (blueprint HTML §A2, verified 06/07 trên mm.nal.vn).
// Ported from avatar-po-cli phase-04 (identical logic — Mattermost API contract
// is CLI-agnostic, same bot/team/channel resolution applies to avatar dev CLI).
// 1. GET /api/v4/users/me → verify username === botName.
// 2. GET /api/v4/users/me/teams → danh sách team bot thuộc.
// 3. GET /api/v4/teams/{team_id}/channels/name/{channelName} → channel obj (id + team_id).
//    Bot thuộc nhiều team → thử từng team, gom hết match (disambiguation — xem dưới).
// 4. GET /plugins/chatops-kanban-plugin/c-{channelId} → xác nhận đọc được board.
// Tách khỏi comm-connect.ts (file-size guideline <200 dòng). Exported để test (mock fetch).
export interface ResolvedMattermostChannel {
  teamId: string;
  channelId: string;
  channelName: string;
  username: string;
}

// Ứng viên channel trùng slug ở nhiều team — trả cho caller prompt chọn khi
// channelInput KHÔNG chỉ định tường minh team (không có dạng "team/channel").
export interface AmbiguousChannelCandidate {
  teamId: string;
  teamName: string; // team slug (name), dùng hiển thị "team/channel".
  teamDisplayName: string; // display_name — thân thiện hơn khi hiển thị.
  channelId: string;
}

// Phân loại lỗi setup Mattermost để caller (retry-loop UX) biết field nào cần
// re-prompt: "token" → hỏi lại password; "channel"/"team"/"other" → hỏi lại tên
// channel. GIỮ NGUYÊN message tiếng Việt hiện có — chỉ thêm field `kind`.
export type MattermostSetupErrorKind = "token" | "team" | "channel" | "other";

export class MattermostSetupError extends Error {
  kind: MattermostSetupErrorKind;

  constructor(message: string, kind: MattermostSetupErrorKind) {
    super(message);
    this.name = "MattermostSetupError";
    this.kind = kind;
  }
}

type MattermostTeam = { id: string; name?: string; display_name?: string };

// channelInput có thể là "channel" (tên trần) hoặc "team/channel" (chỉ định tường
// minh team). Split tại dấu "/" ĐẦU TIÊN — phần channel có thể chứa thêm "/" (hiếm
// nhưng Mattermost cho phép trong tên hiển thị, KHÔNG phải slug — an toàn).
// Trim 2 vế (user hay gõ "team / channel" có space); vế rỗng → throw sớm message
// rõ thay vì để API trả 404 khó hiểu.
function parseChannelInput(channelInput: string): { teamSlug: string | null; channelName: string } {
  const slashIdx = channelInput.indexOf("/");
  if (slashIdx === -1) {
    return { teamSlug: null, channelName: channelInput.trim() };
  }
  const teamSlug = channelInput.slice(0, slashIdx).trim();
  const channelName = channelInput.slice(slashIdx + 1).trim();
  if (teamSlug.length === 0 || channelName.length === 0) {
    throw new MattermostSetupError(
      `Dạng 'team/channel' cần đủ 2 vế (nhận: '${channelInput}'). Ví dụ: nal/spec-review.`,
      "channel",
    );
  }
  return { teamSlug, channelName };
}

// Tìm channel trong 1 team cụ thể theo tên. Trả undefined nếu 404 (không tồn tại
// ở team đó) — KHÔNG throw, để caller quyết định (team chỉ định tường minh vs gom
// candidates từ nhiều team).
async function fetchChannelInTeam(
  baseUrl: string,
  headers: Record<string, string>,
  teamId: string,
  channelName: string,
): Promise<{ id: string; team_id: string } | undefined> {
  const channelRes = await fetch(
    `${baseUrl}/api/v4/teams/${teamId}/channels/name/${encodeURIComponent(channelName)}`,
    { headers },
  );
  if (!channelRes.ok) return undefined;
  return (await channelRes.json()) as { id: string; team_id: string };
}

export async function validateBotTokenAndResolveChannel(
  baseUrl: string,
  token: string,
  botName: string,
  channelInput: string,
  selectTeam?: (candidates: AmbiguousChannelCandidate[]) => Promise<AmbiguousChannelCandidate>,
): Promise<ResolvedMattermostChannel> {
  const headers = { Authorization: `Bearer ${token}` };
  const { teamSlug, channelName } = parseChannelInput(channelInput);

  // Step 1: verify token + botName khớp.
  const meRes = await fetch(`${baseUrl}/api/v4/users/me`, { headers });
  if (!meRes.ok) {
    throw new MattermostSetupError(
      `Mattermost từ chối token (${meRes.status}). Kiểm tra token còn hạn.`,
      "token",
    );
  }
  const me = (await meRes.json()) as { username?: string };
  if (me.username !== botName) {
    throw new MattermostSetupError(
      `Token không thuộc bot '${botName}' (username thực: ${me.username ?? "?"}).`,
      "token",
    );
  }

  // Step 2: teams bot thuộc.
  const teamsRes = await fetch(`${baseUrl}/api/v4/users/me/teams`, { headers });
  if (!teamsRes.ok) {
    throw new MattermostSetupError(`Không lấy được danh sách team (${teamsRes.status}).`, "team");
  }
  const teams = (await teamsRes.json()) as MattermostTeam[];
  if (!teams || teams.length === 0) {
    throw new MattermostSetupError(
      `Bot '${botName}' chưa thuộc team nào — nhờ admin thêm bot vào team.`,
      "team",
    );
  }

  let channel: { id: string; team_id: string } | undefined;

  if (teamSlug !== null) {
    // Chỉ định tường minh "team/channel" → tìm đúng team đó (case-insensitive), chỉ
    // GET channel trong team này. Không thấy team → lỗi "team" liệt kê team bot thuộc.
    const team = teams.find((t) => t.name?.toLowerCase() === teamSlug.toLowerCase());
    if (!team) {
      throw new MattermostSetupError(
        `Bot '${botName}' không thuộc team '${teamSlug}' (hoặc team không tồn tại). Team bot đang thuộc: ${teams.map((t) => t.name).join(", ")}.`,
        "team",
      );
    }
    channel = await fetchChannelInTeam(baseUrl, headers, team.id, channelName);
    if (!channel) {
      throw new MattermostSetupError(
        `Không tìm thấy channel '${teamSlug}/${channelName}' — kiểm tra lại tên channel.`,
        "channel",
      );
    }
  } else {
    // Không chỉ định team → duyệt HẾT teams, gom mọi match (không break sớm) để phát
    // hiện trùng slug ở nhiều team (bug gốc cần fix).
    const candidates: AmbiguousChannelCandidate[] = [];
    for (const team of teams) {
      const found = await fetchChannelInTeam(baseUrl, headers, team.id, channelName);
      if (found) {
        candidates.push({
          teamId: team.id,
          teamName: team.name ?? team.id,
          teamDisplayName: team.display_name ?? team.name ?? team.id,
          channelId: found.id,
        });
      }
    }

    if (candidates.length === 0) {
      throw new MattermostSetupError(
        `Không tìm thấy channel '${channelName}' — kiểm tra lại tên channel.`,
        "channel",
      );
    }

    if (candidates.length === 1) {
      // 1 match → backward-compat, dùng luôn, KHÔNG gọi selectTeam.
      const only = candidates[0] as AmbiguousChannelCandidate;
      channel = { id: only.channelId, team_id: only.teamId };
    } else if (selectTeam) {
      const chosen = await selectTeam(candidates);
      channel = { id: chosen.channelId, team_id: chosen.teamId };
    } else {
      throw new MattermostSetupError(
        `Channel '${channelName}' tồn tại ở ${candidates.length} team (${candidates.map((c) => c.teamName).join(", ")}). Gõ dạng 'team/channel' để chỉ định.`,
        "channel",
      );
    }
  }

  // Step 4: xác nhận đọc được board Kanban trên channel.
  const boardRes = await fetch(`${baseUrl}/plugins/chatops-kanban-plugin/c-${channel.id}`, {
    headers,
  });
  if (!boardRes.ok) {
    throw new MattermostSetupError(
      `Không đọc được board Kanban trên channel '${channelName}' (${boardRes.status}).`,
      "channel",
    );
  }

  return {
    teamId: channel.team_id,
    channelId: channel.id,
    channelName,
    username: me.username,
  };
}
